import type {
  TSurvey,
  TSurveyChoice,
  TSurveyComboRule,
  TSurveyQuestion,
  TSurveyQuestionInput,
  TSurveyReminder,
  TSurveyStatus,
  TUpsertSurveyRequest,
} from 'api/surveys'
import { v4 as uuidv4 } from 'uuid'

const MIN_CHOICES = 2

export const MAX_REMINDER_MESSAGE_LENGTH = 1000
export const MAX_COMBO_RULES = 20
export const MAX_COMBO_CONDITIONS = 10
export const MAX_SURVEY_RESPONSE_LIMIT = 100
export const DEFAULT_REMINDER_TIME = '09:00'
export const SURVEY_REMINDER_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

export const SURVEY_STATUS_OPTIONS: ReadonlyArray<{
  value: TSurveyStatus
  label: string
}> = [
  { value: 'DRAFT', label: '下書き' },
  { value: 'ACTIVE', label: '公開中' },
  { value: 'CLOSED', label: '終了' },
]

export const getAllowedSurveyStatusOptions = (
  persistedStatus: TSurveyStatus,
): ReadonlyArray<{ value: TSurveyStatus; label: string }> => {
  if (persistedStatus === 'DRAFT') return SURVEY_STATUS_OPTIONS
  if (persistedStatus === 'ACTIVE') {
    return [
      { value: 'ACTIVE', label: '公開中' },
      { value: 'CLOSED', label: '終了' },
    ]
  }
  return [
    { value: 'CLOSED', label: '終了' },
    { value: 'ACTIVE', label: '公開中' },
  ]
}

let clientIdSequence = 0

const createClientId = (prefix: string) => {
  clientIdSequence += 1
  return `new-${prefix}-${clientIdSequence}`
}

export type TSurveyBuilderChoice = TSurveyChoice & {
  clientId: string
}

export type TSurveyBuilderQuestion = Omit<
  TSurveyQuestion,
  'choices' | 'maxSelections'
> & {
  clientId: string
  maxSelections?: number
  choices?: TSurveyBuilderChoice[]
}

export type TSurveyBuilderReminder = {
  clientId: string
} & TSurveyReminder

export type TSurveyBuilderComboCondition = {
  clientId: string
  questionClientId: string
  choiceClientId: string
}

export type TSurveyBuilderComboRule = {
  clientId: string
  id?: number
  tagId: number | ''
  conditions: TSurveyBuilderComboCondition[]
}

export const createEmptyQuestion = (
  type: TSurveyQuestion['type'] = 'SINGLE',
): TSurveyBuilderQuestion => ({
  clientId: createClientId('question'),
  type,
  title: '',
  required: true,
  choices:
    type === 'SINGLE' || type === 'MULTI'
      ? Array.from({ length: MIN_CHOICES }, (_, index) => ({
          clientId: createClientId('choice'),
          label: `選択肢${index + 1}`,
          tagIds: [],
        }))
      : undefined,
})

export const createEmptyChoice = (choiceNumber: number): TSurveyBuilderChoice => ({
  clientId: createClientId('choice'),
  label: `選択肢${choiceNumber}`,
  tagIds: [],
})

export const createEmptyReminder = (): TSurveyBuilderReminder => {
  const id = uuidv4()
  return {
    id,
    clientId: `reminder-${id}`,
    days: 1,
    time: DEFAULT_REMINDER_TIME,
    message: 'アンケートへのご回答をお願いします。',
  }
}

const normalizeReminderTime = (time: string | undefined): string =>
  time && SURVEY_REMINDER_TIME_PATTERN.test(time)
    ? time
    : DEFAULT_REMINDER_TIME

const normalizeQuestionImage = (
  question: Pick<TSurveyQuestion, 'imageUrl' | 'imageAspectRatio'>,
): Pick<TSurveyQuestionInput, 'imageUrl' | 'imageAspectRatio'> => {
  const imageUrl = question.imageUrl?.trim() || undefined
  const imageAspectRatio =
    question.imageAspectRatio === '1:1' || question.imageAspectRatio === '16:9'
      ? question.imageAspectRatio
      : undefined
  if (!imageUrl || !imageAspectRatio) {
    return {}
  }
  return { imageUrl, imageAspectRatio }
}

export const createEmptyComboCondition = (): TSurveyBuilderComboCondition => ({
  clientId: createClientId('combo-condition'),
  questionClientId: '',
  choiceClientId: '',
})

export const createEmptyComboRule = (): TSurveyBuilderComboRule => ({
  clientId: createClientId('combo-rule'),
  tagId: '',
  conditions: [createEmptyComboCondition()],
})

export const hydrateSurveyBuilder = (
  survey: TSurvey,
): {
  questions: TSurveyBuilderQuestion[]
  reminders: TSurveyBuilderReminder[]
  comboRules: TSurveyBuilderComboRule[]
} => {
  const questions = [...survey.questions]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map<TSurveyBuilderQuestion>((question) => ({
      id: question.id,
      clientId: `question-${question.id}`,
      type: question.type,
      title: question.title,
      required: question.required,
      maxSelections: question.maxSelections ?? undefined,
      ...normalizeQuestionImage(question),
      choices:
        question.type === 'SINGLE' || question.type === 'MULTI'
          ? (question.choices ?? []).map((choice) => ({
              id: choice.id,
              clientId: `choice-${choice.id}`,
              label: choice.label,
              tagIds: choice.tagIds,
            }))
          : undefined,
    }))

  const questionClientIdById = new Map(
    questions.flatMap((question) =>
      question.id === undefined ? [] : [[question.id, question.clientId] as const],
    ),
  )
  const choiceClientIdById = new Map(
    questions.flatMap((question) =>
      (question.choices ?? []).flatMap((choice) =>
        choice.id === undefined ? [] : [[choice.id, choice.clientId] as const],
      ),
    ),
  )

  const comboRules = (survey.comboRules ?? []).map<TSurveyBuilderComboRule>(
    (rule) => ({
      id: rule.id,
      clientId: `combo-rule-${rule.id}`,
      tagId: rule.tagId,
      conditions: rule.conditions.map((condition, conditionIndex) => {
        const questionClientId = questionClientIdById.get(condition.questionId)
        const choiceClientId = choiceClientIdById.get(condition.choiceId)
        if (!questionClientId || !choiceClientId) {
          throw new Error('組み合わせタグの参照先を読み込めませんでした。')
        }
        return {
          clientId: `combo-condition-${rule.id}-${conditionIndex}`,
          questionClientId,
          choiceClientId,
        }
      }),
    }),
  )

  return {
    questions,
    reminders: serializeReminders(survey.reminders ?? []).map((reminder) => ({
      clientId: `reminder-${reminder.id}`,
      id: reminder.id,
      days: reminder.days,
      time: normalizeReminderTime(reminder.time),
      message: reminder.message,
    })),
    comboRules,
  }
}

const normalizeQuestionForSubmit = (
  question: TSurveyBuilderQuestion,
): TSurveyQuestionInput => {
  const baseQuestion: TSurveyQuestionInput = {
    id: question.id,
    type: question.type,
    title: question.title,
    required: question.required,
    ...normalizeQuestionImage(question),
  }

  if (question.type === 'TEXT' || question.type === 'DATE') {
    return baseQuestion
  }

  return {
    ...baseQuestion,
    maxSelections:
      question.type === 'MULTI' ? question.maxSelections : undefined,
    choices: (question.choices ?? []).map((choice) => ({
      id: choice.id,
      label: choice.label,
      tagIds: [...new Set(choice.tagIds ?? [])],
    })),
  }
}

export const serializeComboRules = (
  questions: TSurveyBuilderQuestion[],
  comboRules: TSurveyBuilderComboRule[],
): TSurveyComboRule[] => {
  if (comboRules.length > MAX_COMBO_RULES) {
    throw new Error(`組み合わせタグは最大${MAX_COMBO_RULES}件です。`)
  }

  return comboRules.map((rule) => {
    if (rule.tagId === '') {
      throw new Error('組み合わせタグの付与タグを選択してください。')
    }
    if (rule.conditions.length === 0) {
      throw new Error('組み合わせタグには条件を1つ以上設定してください。')
    }
    if (rule.conditions.length > MAX_COMBO_CONDITIONS) {
      throw new Error(
        `組み合わせタグの条件は最大${MAX_COMBO_CONDITIONS}件です。`,
      )
    }

    const resolvedConditions = rule.conditions.map((condition) => {
      const questionIndex = questions.findIndex(
        (question) => question.clientId === condition.questionClientId,
      )
      const choiceIndex =
        questionIndex < 0
          ? -1
          : (questions[questionIndex].choices ?? []).findIndex(
              (choice) => choice.clientId === condition.choiceClientId,
            )
      if (questionIndex < 0 || choiceIndex < 0) {
        throw new Error('組み合わせタグの設問と選択肢を指定してください。')
      }
      const question = questions[questionIndex]
      const choice = question.choices?.[choiceIndex]
      return { question, questionIndex, choice, choiceIndex }
    })

    const choiceIdsByQuestion = new Map<string, Set<string>>()
    for (const condition of rule.conditions) {
      const choiceIds =
        choiceIdsByQuestion.get(condition.questionClientId) ??
        new Set<string>()
      choiceIds.add(condition.choiceClientId)
      choiceIdsByQuestion.set(condition.questionClientId, choiceIds)
    }
    for (const question of questions) {
      const selectedCount =
        choiceIdsByQuestion.get(question.clientId)?.size ?? 0
      if (question.type === 'SINGLE' && selectedCount > 1) {
        throw new Error(
          `単一選択の設問「${question.title}」に複数の選択肢を指定できません。`,
        )
      }
      if (
        question.type === 'MULTI' &&
        question.maxSelections !== undefined &&
        selectedCount > question.maxSelections
      ) {
        throw new Error(
          `複数選択の設問「${question.title}」は最大選択数${question.maxSelections}まで指定できます。`,
        )
      }
    }

    const conditions = resolvedConditions.map(
      ({ question, questionIndex, choice, choiceIndex }) => {
        const hasExistingIds =
          question.id !== undefined && choice?.id !== undefined
        if (hasExistingIds) {
          return { questionId: question.id!, choiceId: choice!.id! }
        }
        return { questionIndex, choiceIndex }
      },
    )

    const conditionKeys = conditions.map(
      (condition) =>
        'questionId' in condition
          ? `id:${condition.questionId}:${condition.choiceId}`
          : `index:${condition.questionIndex}:${condition.choiceIndex}`,
    )
    if (new Set(conditionKeys).size !== conditionKeys.length) {
      throw new Error('同じ組み合わせ条件を重複して指定できません。')
    }

    return { tagId: rule.tagId, conditions }
  })
}

export const serializeReminders = (
  reminders: ReadonlyArray<TSurveyReminder>,
): TSurveyReminder[] => {
  const reminderIds = reminders.map((reminder) => reminder.id)
  if (new Set(reminderIds).size !== reminderIds.length) {
    throw new Error('同じリマインドIDを重複して指定できません。')
  }

  return reminders
    .map(({ id, days, message, time }) => ({
      id,
      days,
      time: normalizeReminderTime(time),
      message,
    }))
    .sort((left, right) => left.days - right.days)
}

export const buildSurveyPayload = (
  values: Omit<TUpsertSurveyRequest, 'questions' | 'comboRules' | 'reminders'> & {
    questions: TSurveyBuilderQuestion[]
    reminders: TSurveyBuilderReminder[]
    comboRules: TSurveyBuilderComboRule[]
  },
): TUpsertSurveyRequest => ({
  ...values,
  reminders: serializeReminders(values.reminders),
  questions: values.questions.map(normalizeQuestionForSubmit),
  comboRules: serializeComboRules(values.questions, values.comboRules),
})
